import React from 'react';
import type { MockOrder, MockViewerRole } from '../types';

export interface ActivityTabProps {
  order: MockOrder;
  viewer: MockViewerRole;
}

interface ActivityEntry {
  id: string;
  /** What changed (verb-led). */
  action: string;
  /** Who did it. */
  actor: string;
  /** Optional payload preview. */
  payload?: string;
  at: Date;
  /** Visibility tier. Some entries are commercial-only and only show for buyer/seller of a leg. */
  scope: 'absolute' | 'commercial' | 'system';
}

const scopePill: Record<ActivityEntry['scope'], { label: string; className: string }> = {
  absolute: {
    label: 'Visible to all',
    className: 'bg-gray-100 text-gray-700 ring-gray-200',
  },
  commercial: {
    label: 'Commercial',
    className: 'bg-amber-50 text-amber-700 ring-amber-200',
  },
  system: {
    label: 'System',
    className: 'bg-slate-100 text-slate-700 ring-slate-200',
  },
};

const HOUR = 60 * 60_000;

const formatRelative = (d: Date) => {
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/**
 * Build a fake commercial-aware activity log. The `commercial`-scoped
 * entries are filtered out for `driver` viewers (who never see
 * pricing) — that's the same rule `useOrderViewerLens` will enforce
 * in production.
 */
const buildActivity = (order: MockOrder): ActivityEntry[] => {
  const t0 = order.createdAt.getTime();
  const out: ActivityEntry[] = [
    {
      id: 'a-create',
      action: 'Order created',
      actor: order.shipper.name,
      payload: `${order.pickup.city} → ${order.dropoff.city}`,
      at: order.createdAt,
      scope: 'absolute',
    },
    {
      id: 'a-search',
      action: 'Capacity search started',
      actor: 'system',
      payload: `Strategy: ${order.pricingMode.replace('_', ' ')}`,
      at: new Date(t0 + 0.2 * HOUR),
      scope: 'system',
    },
  ];

  order.legs.forEach((leg, idx) => {
    out.push({
      id: `a-leg-${idx}-priced`,
      action: `Leg ${idx + 1} priced`,
      actor: leg.buyer,
      payload: `${leg.amount.toLocaleString()} ${leg.currency} (${leg.pricingSource})`,
      at: new Date(t0 + (0.4 + idx * 0.05) * HOUR),
      scope: 'commercial',
    });
    out.push({
      id: `a-leg-${idx}-accepted`,
      action: `Leg ${idx + 1} accepted`,
      actor: leg.seller,
      at: new Date(t0 + (0.5 + idx * 0.05) * HOUR),
      scope: 'commercial',
    });
  });

  if (
    order.driver &&
    (order.status === 'assigned' ||
      order.status === 'in_transit' ||
      order.status === 'delivered')
  ) {
    out.push({
      id: 'a-driver',
      action: 'Driver assigned',
      actor: order.carrier?.name ?? 'Carrier',
      payload: order.driver.name,
      at: new Date(t0 + 1.5 * HOUR),
      scope: 'absolute',
    });
  }

  if (order.status === 'in_transit' || order.status === 'delivered') {
    out.push({
      id: 'a-pickup',
      action: 'Picked up',
      actor: order.driver?.name ?? 'Driver',
      payload: order.pickup.city,
      at: new Date(t0 + 3 * HOUR),
      scope: 'absolute',
    });
  }

  if (order.status === 'delivered') {
    out.push({
      id: 'a-delivered',
      action: 'Delivered',
      actor: order.driver?.name ?? 'Driver',
      payload: `Signed at ${order.dropoff.city}`,
      at: new Date(t0 + 8 * HOUR),
      scope: 'absolute',
    });
  }

  return out.sort((a, b) => b.at.getTime() - a.at.getTime());
};

/**
 * Audit-log style feed. Drivers do not see commercial-scoped entries
 * (mock filter); everyone else sees everything in this dummy.
 */
export const ActivityTab: React.FC<ActivityTabProps> = ({ order, viewer }) => {
  const all = buildActivity(order);
  const visible = all.filter(e => viewer !== 'driver' || e.scope !== 'commercial');

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <ul className="divide-y divide-gray-100">
        {visible.map(entry => {
          const pill = scopePill[entry.scope];
          return (
            <li key={entry.id} className="flex items-start gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[13px] font-medium text-gray-900">
                    {entry.action}
                  </p>
                  <span
                    className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${pill.className}`}>
                    {pill.label}
                  </span>
                </div>
                {entry.payload && (
                  <p className="mt-0.5 text-[12px] text-gray-600">{entry.payload}</p>
                )}
                <p className="mt-0.5 text-[11px] text-gray-400">
                  {entry.actor === 'system' ? 'System' : entry.actor}
                  <span className="mx-1.5 text-gray-300">·</span>
                  <time>{formatRelative(entry.at)}</time>
                </p>
              </div>
            </li>
          );
        })}
        {visible.length === 0 && (
          <li className="px-4 py-6 text-center text-[12px] text-gray-500">
            No activity visible to this viewer yet.
          </li>
        )}
      </ul>
    </div>
  );
};
